import { PropsWithChildren, createContext, useContext, useEffect, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import type { AllFeatureIds } from 'generated/featureIds.generated'
import {
  LastSyncRunDocument,
  MarketplaceDbIntegration,
  ReadSyncValidationsDocument,
  SyncCompensationsDocument,
  SyncFrameworkAvailabilityDocument,
  SyncRunStatusChangeDocument,
  SyncableStateFragmentDoc,
  SyncableStateUpdatedDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useSubscription } from 'modules/core/hooks/useSubscription'
import { handleResult } from 'modules/core/lib/handleMutation'

import {
  Validation,
  useCompensationsSyncValidations,
} from 'modules/integrations/hooks/SyncContext/validations'
import { SyncRunStatus } from 'modules/integrations/lib/syncRun'
import integrationCodes from 'modules/marketplace/lib/integrationCodes'

const A3INNUVA_SYNC_PAYROLL_COMPENSATIONS_FEATURE_FLAG_NAME: AllFeatureIds =
  'dev_a3innuva_sync_payroll_compensations'
const DATEV_SYNC_PAYROLL_COMPENSATIONS_FEATURE_FLAG_NAME: AllFeatureIds =
  'dev_datev_lauds_sync_payroll_compensations'

interface SyncContextType {
  syncCompensations: () => Promise<void>
  syncValidations: Validation[]
  syncLoading: boolean
  isSyncRunning: boolean
  showModal: boolean
  closeModal: () => void
  openModal: () => void
  setPayrollRunId: (payrollRunId: string | null) => void
  refetchSyncValidations: () => void
  syncValidationsLoading: boolean
  marketplaceIntegration: MarketplaceDbIntegration | null
  syncEnabled: boolean
}

const initialState: SyncContextType = {
  syncCompensations: async () => {},
  syncValidations: [],
  syncLoading: false,
  isSyncRunning: false,
  showModal: false,
  closeModal: () => {},
  openModal: () => {},
  setPayrollRunId: () => {},
  refetchSyncValidations: () => {},
  syncValidationsLoading: false,
  marketplaceIntegration: null,
  syncEnabled: false,
}

const SyncContext = createContext<SyncContextType>(initialState)

export const IntegrationsSyncContextProvider = ({ children }: PropsWithChildren) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const { navigation } = useNavigation()

  const [isSyncRunning, setIsSyncRunRunning] = useState(false)
  const [payrollRunId, setPayrollRunId] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const client = useApolloClient()

  // TODO: we are hacking this to unblock DATEV, but we need to properly handle the integrations
  // availability
  const { data: a3innuvaFrameworkAvailabilityData } = useQuery(SyncFrameworkAvailabilityDocument, {
    variables: {
      companyId,
      featureFlagNames: [A3INNUVA_SYNC_PAYROLL_COMPENSATIONS_FEATURE_FLAG_NAME],
      integrationDatoIds: [integrationCodes.a3innuva],
    },
  })

  const { data: datevFrameworkAvailabilityData } = useQuery(SyncFrameworkAvailabilityDocument, {
    variables: {
      companyId,
      featureFlagNames: [DATEV_SYNC_PAYROLL_COMPENSATIONS_FEATURE_FLAG_NAME],
      integrationDatoIds: [integrationCodes.datev_lauds],
    },
  })

  const ffEnabled =
    a3innuvaFrameworkAvailabilityData?.features?.featuresConnection.nodes[0]?.enabled || false
  const marketplaceIntegration =
    a3innuvaFrameworkAvailabilityData?.marketplace?.companyInstallationsConnection.nodes[0]
      ?.dbIntegration ||
    datevFrameworkAvailabilityData?.marketplace?.companyInstallationsConnection.nodes[0]
      ?.dbIntegration ||
    null
  const syncEnabled = ffEnabled && !!marketplaceIntegration

  const [sync, { loading: syncLoading }] = useMutation(SyncCompensationsDocument, {
    extractorFn: (data) => data.integrationsMutations.createIntegrationsSyncRun,
  })

  useSubscription(SyncRunStatusChangeDocument, {
    onData: ({ data: subscriptionData }) => {
      let newIsRunning = isSyncRunning
      if (subscriptionData.data?.integrationsSyncRunStatusChange?.syncRun?.status) {
        newIsRunning =
          subscriptionData.data?.integrationsSyncRunStatusChange?.syncRun?.status ===
          SyncRunStatus.RUNNING
        setIsSyncRunRunning(newIsRunning)
      }
    },
    skip: !syncEnabled,
  })

  useSubscription(SyncableStateUpdatedDocument, {
    onData: ({ data: subscriptionData }) => {
      const syncableState = subscriptionData.data?.integrationsSyncableStateUpdate?.syncableState
      if (
        !syncableState ||
        syncableState.syncableType !== 'compensationscompensation' ||
        !marketplaceIntegration
      )
        return
      const compensationKey = `CompensationsPayrollRunEmployeesCompensation:${syncableState.syncableId}`

      client.writeFragment({
        id: compensationKey,
        variables: {
          marketplaceIntegrationId: marketplaceIntegration.id,
          syncableType: 'compensationscompensation',
        },
        fragment: SyncableStateFragmentDoc,
        data: {
          syncableState: [
            {
              id: syncableState.id,
              status: syncableState.status,
              errorMessages: syncableState.errorMessages,
              marketplaceIntegrationId: marketplaceIntegration.id,
              statusUpdatedAt: syncableState.statusUpdatedAt,
              syncableId: syncableState.syncableId,
              syncableType: syncableState.syncableType,
              __typename: 'IntegrationsSyncableState',
            },
          ],
          __typename: 'CompensationsPayrollRunEmployeesCompensation',
        },
      })
    },
    skip: !syncEnabled,
  })

  const syncCompensations = async () => {
    if (!payrollRunId || !syncEnabled || !marketplaceIntegration) return

    setIsSyncRunRunning(true)
    closeModal()
    const response = await sync({
      variables: {
        companyId,
        marketplaceIntegrationId: marketplaceIntegration.id,
        payrollRunId: payrollRunId.toString(),
      },
    })
    const result = handleResult(response, () => undefined)
    if (result.success === false) setIsSyncRunRunning(false)
  }

  const { data: syncRunData } = useQuery(LastSyncRunDocument, {
    variables: {
      marketplaceIntegrationId: marketplaceIntegration?.id || 0,
    },
    skip: !syncEnabled,
  })
  const syncRunStatus = syncRunData?.integrations?.syncRunsConnection.nodes[0]?.status

  useEffect(() => {
    syncRunStatus === SyncRunStatus.RUNNING && setIsSyncRunRunning(true)
  }, [syncRunStatus])

  const closeModal = () => {
    setShowModal(false)
  }

  const openModal = () => {
    setShowModal(true)
  }

  const {
    data: syncValidationsData,
    refetch: refetchSyncValidations,
    loading: syncValidationsLoading,
  } = useQuery(ReadSyncValidationsDocument, {
    variables: {
      companyId,
      marketplaceIntegrationId: marketplaceIntegration?.id || 0,
      payrollRunId: payrollRunId || '',
    },
    skip: !syncEnabled || !payrollRunId,
  })

  const blockingIssues =
    syncValidationsData?.integrations?.syncValidationsConnection.nodes[0]?.blockingIssues || []

  return (
    <SyncContext.Provider
      value={{
        syncCompensations,
        refetchSyncValidations,
        syncValidations: useCompensationsSyncValidations(blockingIssues, marketplaceIntegration),
        syncLoading,
        isSyncRunning,
        showModal,
        closeModal,
        openModal,
        setPayrollRunId,
        syncValidationsLoading,
        marketplaceIntegration,
        syncEnabled,
      }}
    >
      {children}
    </SyncContext.Provider>
  )
}

export function useIntegrationsSyncContext() {
  const context = useContext(SyncContext)

  if (!context) {
    throw new Error('useIntegrationsSyncContext must be used within a SyncContextProvider')
  }

  return context
}
